import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMe } from '../../../store';
import { useBundleCheck } from '../../../utils/useBundleCheck';
import {
    BundleDeviceParamItem,
    BundleIssue,
    BundleModeRef,
    BundleParamSchemaKey,
    BundleModeSwitch,
    BundleReport,
    BundleThermalCal,
    DeviceBundlePayload,
    submitDeviceBundle,
} from '../../../api/bundle';

// 设备创建向导的状态层 (2026-09-17, 设计 doc/relate-import/07 P2).
//
// 与材料向导同一套路: **向导状态 = 一份设备套件载荷**, 每步"下一步"拿当前载荷跑 dryRun,
// 报告按 path 回贴到对应步骤的字段. 前端不复刻任何校验规则 —— 模式字典、字段组模板、
// 标定曲线的单调性与端点判定全在服务端, 抄一份必然漂移.
//
// 没有把材料那份 hook 泛型化: 两边的 STEPS、path→步骤映射、空值归一规则都不一样,
// 抽成泛型后调用处要塞进去的配置比现在多; 真正值得共用的 (草稿存取、离开拦截、
// issue 聚合) 是纯逻辑, 复制 30 行比造一层抽象划算。

/** 向导步骤 (① 必填, ②③④⑤ 可选, ⑥ 提交).
 *
 * 2026-09-22 起向导 = 单设备版的套件文件导入: 后端 DeviceBundle 的每一段在向导里都有落点
 * (device / modeRefs / modeSwitches / extValues / calibration / thermalCal / schemaBindings / tips / params),
 * 不再只覆盖一个子集 —— 此前 6 头机型走向导要点 6 遍还得回头去工况页补速度、去参数页补参数。
 *
 * 第 ⑤ 步"加工参数": 字段按解析到的参数模板渲染 (与参数管理页同一口径), 字典只作补位;
 * 参数模板按设备建 (2026-11-01): 第 ⑤ 步各行选用 / 复制来源的模板, 提交时转成 schemaBindings.paramSchemas,
 * 审批通过时给新设备各复制一份 (见 paramSchemaCopies)。 */
export const DEVICE_STEPS = [
    { key: 'basic', label: '基本信息', optional: false },
    { key: 'modes', label: '加工模式', optional: true },
    { key: 'fields', label: '设备字段组', optional: true },
    { key: 'calib', label: '功率标定', optional: true },
    { key: 'params', label: '加工参数', optional: true },
    { key: 'review', label: '预检与提交', optional: false },
] as const;

export type DeviceStepKey = (typeof DEVICE_STEPS)[number]['key'];

/** stepOf issue 的 path → 归属步骤 (与后端报告的 path 前缀一一对应). */
export function stepOf(path: string): DeviceStepKey {
    if (
        path.startsWith('modeRefs') ||
        path.startsWith('modeSwitches') ||
        path.startsWith('newDicts.modeGroups') ||
        path.startsWith('newDicts.modes')
    )
        return 'modes';
    if (path.startsWith('newDicts.deviceSchemas')) return 'fields';
    if (path.startsWith('extValues')) return 'fields';
    if (path.startsWith('calibration') || path.startsWith('thermalCal'))
        return 'calib';
    if (path.startsWith('params')) return 'params';
    // schemaBindings.paramSchemas 在第 ⑤ 步; deviceSchemas 由第 ③ 步的"需绑定"勾选产生
    if (path.startsWith('schemaBindings.paramSchemas')) return 'params';
    if (path.startsWith('schemaBindings')) return 'fields';
    return 'basic'; // device.* 与结构层错误都回第一步
}

export type IssueByPath = Record<string, BundleIssue>;

const DRAFT_KEY_BASE = 'device-wizard-draft-v1';

/**
 * draftKeyOf 草稿按登录用户分开存 (2026-09-21).
 * 草稿只在浏览器本地, key 原先是固定的 —— 共享工位/演示机上 A 退出、B 登录,
 * B 一进向导就被 A 的半张表单"恢复"出来。带上 username 后各人各一份;
 * 拿不到用户名 (me 还没加载完) 时不读也不写, 宁可不恢复也不串号。
 */
export function draftKeyOf(username: string): string {
    return username ? `${DRAFT_KEY_BASE}:${username}` : '';
}
const DRAFT_DEBOUNCE_MS = 1000;

export function emptyDevicePayload(): DeviceBundlePayload {
    return { device: { deviceKey: '', family: '', archCode: '' } };
}

interface DraftEnvelope {
    payload: DeviceBundlePayload;
    step: DeviceStepKey;
    savedAt: number;
}

export function loadDraft(username: string): DraftEnvelope | null {
    const key = draftKeyOf(username);
    if (!key) return null;
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const d = JSON.parse(raw) as DraftEnvelope;
        if (!d?.payload?.device) return null;
        return d;
    } catch {
        return null;
    }
}

export function clearDraft(username: string): void {
    const key = draftKeyOf(username);
    if (!key) return;
    try {
        localStorage.removeItem(key);
    } catch {
        /* 隐私模式 / 站点数据被禁: 草稿只是便利功能 */
    }
}

/** dropLegacyDraft 清掉 2026-09-21 之前那份不带用户名的旧草稿: 它不再被读取,
 * 留着只会在共享浏览器里继续躺着别人的填写内容. */
function dropLegacyDraft(): void {
    try {
        localStorage.removeItem(DRAFT_KEY_BASE);
    } catch {
        /* 隐私模式 / 站点数据被禁 */
    }
}

export function hasContent(p: DeviceBundlePayload): boolean {
    const d = p.device;
    return !!(
        d.deviceKey ||
        d.family ||
        d.archCode ||
        d.name ||
        p.modeRefs?.length ||
        p.modeSwitches?.length ||
        p.calibration?.length ||
        p.thermalCal?.length ||
        p.params?.length ||
        (p.extValues && Object.keys(p.extValues).length) ||
        p.schemaBindings?.deviceSchemas?.length ||
        p.schemaBindings?.paramSchemas?.length ||
        newDictCount(p.newDicts) > 0
    );
}

/** newDictCount 随套件新建的字典项总数 (模式组 + 模式值 + 字段组). */
export function newDictCount(nd: DeviceBundlePayload['newDicts']): number {
    return (
        (nd?.modeGroups?.length || 0) +
        (nd?.modes?.length || 0) +
        (nd?.deviceSchemas?.length || 0)
    );
}

/**
 * paramSchemaCopies 要给新设备复制的参数模板 (schemaBindings.paramSchemas): 参数行的「字段来源模板」
 * (fieldsFrom = deviceKey|family|group|processType, 手选或从已有设备复制时带上) + 草稿里原有的, 按键去重。
 * 模板按设备建, 新设备没有自己的模板, 不复制的话这些行在套件预检里会报「没有参数模板命中」;
 * 新设备已有同键模板 (在「参数模板」卡里存过) 时后端跳过复制。
 */
function paramSchemaCopies(p: DeviceBundlePayload): BundleParamSchemaKey[] {
    const out = new Map<string, BundleParamSchemaKey>();
    const add = (k: BundleParamSchemaKey) => {
        const key = [k.deviceKey || '', k.family || '', k.groupCode || '', k.processType || ''].join('|');
        if (!out.has(key)) out.set(key, k);
    };
    (p.schemaBindings?.paramSchemas || []).forEach(add);
    (p.params || []).forEach((r) => {
        if (r.fieldMode === 'custom' || !r.fieldsFrom) return;
        const seg = r.fieldsFrom.split('|');
        if (seg.length !== 4) return; // 2026-11-01 前的草稿键 (family|group|processType), 不认
        const [deviceKey, family, groupCode, processType] = seg;
        add(deviceKey ? { deviceKey, groupCode, processType } : { family, groupCode, processType });
    });
    return Array.from(out.values());
}

/**
 * toRequest 归一成提交载荷: 空字符串/空数组/空对象一律不发.
 * 字段组里被清空的字段直接去键 —— 后端对"填了空串"和"没填"判得不一样。
 */
export function toRequest(p: DeviceBundlePayload): DeviceBundlePayload {
    const device: Record<string, unknown> = {};
    Object.entries(p.device).forEach(([k, v]) => {
        if (v === '' || v === undefined || v === null) return;
        device[k] = v;
    });
    const out: DeviceBundlePayload = {
        device: device as unknown as DeviceBundlePayload['device'],
    };
    if (p.modeRefs?.length) {
        out.modeRefs = p.modeRefs.map((r) => {
            const item: Record<string, unknown> = { modeValue: r.modeValue };
            if (r.enabled !== undefined) item.enabled = r.enabled;
            if (r.aliasZh) item.aliasZh = r.aliasZh;
            if (r.aliasEn) item.aliasEn = r.aliasEn;
            if (r.speedMin !== undefined && r.speedMin !== null)
                item.speedMin = r.speedMin;
            if (r.speedMax !== undefined && r.speedMax !== null)
                item.speedMax = r.speedMax;
            return item as unknown as BundleModeRef;
        });
    }
    if (p.extValues) {
        const groups: NonNullable<DeviceBundlePayload['extValues']> = {};
        Object.entries(p.extValues).forEach(([g, vals]) => {
            const kept: Record<string, unknown> = {};
            Object.entries(vals || {}).forEach(([k, v]) => {
                if (v === '' || v === undefined || v === null) return;
                // 矩阵组的行是对象: 行内全空时整行去掉
                if (typeof v === 'object' && !Array.isArray(v)) {
                    const row: Record<string, unknown> = {};
                    Object.entries(v as Record<string, unknown>).forEach(
                        ([rk, rv]) => {
                            if (rv === '' || rv === undefined || rv === null)
                                return;
                            row[rk] = rv;
                        },
                    );
                    if (Object.keys(row).length) kept[k] = row;
                    return;
                }
                kept[k] = v;
            });
            if (Object.keys(kept).length) groups[g] = kept;
        });
        if (Object.keys(groups).length) out.extValues = groups;
    }
    if (p.modeSwitches?.length) {
        const sw = p.modeSwitches
            .filter((m) => m.mode.trim())
            .map((m) => {
                const item: Record<string, unknown> = {
                    mode: m.mode.trim(),
                    isEnabled: m.isEnabled,
                };
                if (isNum(m.speedMin)) item.speedMin = m.speedMin;
                if (isNum(m.speedMax)) item.speedMax = m.speedMax;
                if (m.note) item.note = m.note;
                return item as unknown as BundleModeSwitch;
            });
        if (sw.length) out.modeSwitches = sw;
    }
    if (p.calibration?.length) out.calibration = p.calibration;
    if (p.thermalCal?.length) {
        const tc = p.thermalCal
            .filter((t) => t.mode.trim() || t.materialType.trim())
            .map((t) => {
                const item: Record<string, unknown> = {
                    mode: t.mode.trim(),
                    materialType: t.materialType.trim(),
                };
                if (isNum(t.tau)) item.tau = t.tau;
                if (isNum(t.beta)) item.beta = t.beta;
                return item as unknown as BundleThermalCal;
            });
        if (tc.length) out.thermalCal = tc;
    }
    const bindings: NonNullable<DeviceBundlePayload['schemaBindings']> = {};
    if (p.schemaBindings?.deviceSchemas?.length) {
        bindings.deviceSchemas = p.schemaBindings.deviceSchemas;
    }
    const tplCopies = paramSchemaCopies(p);
    if (tplCopies.length) bindings.paramSchemas = tplCopies;
    if (Object.keys(bindings).length) out.schemaBindings = bindings;
    if (p.tips?.tipCodes?.length) out.tips = p.tips;
    if (newDictCount(p.newDicts) > 0) {
        const nd: NonNullable<DeviceBundlePayload['newDicts']> = {};
        if (p.newDicts?.modeGroups?.length)
            nd.modeGroups = p.newDicts.modeGroups;
        if (p.newDicts?.modes?.length) nd.modes = p.newDicts.modes;
        if (p.newDicts?.deviceSchemas?.length)
            nd.deviceSchemas = p.newDicts.deviceSchemas;
        out.newDicts = nd;
    }
    if (p.params?.length) {
        out.params = p.params.map((r) => {
            const item: Record<string, unknown> = {
                params: compactParams(r.params),
            };
            (
                [
                    'xcode',
                    'filmXcode',
                    'substrateXcode',
                    'type2',
                    'type1',
                    'subProcess',
                    'resultTag',
                    'remark',
                ] as const
            ).forEach((k) => {
                const v = r[k];
                if (v) item[k] = v;
            });
            return item as unknown as BundleDeviceParamItem;
        });
    }
    return out;
}

function isNum(v: unknown): v is number {
    return typeof v === 'number' && Number.isFinite(v);
}

/** compactParams 参数值去空: 空串/null/undefined 一律不发 (后端对"填了空串"和"没填"判得不一样). */
function compactParams(
    params: Record<string, unknown> | undefined,
): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    Object.entries(params || {}).forEach(([k, v]) => {
        if (v === '' || v === undefined || v === null) return;
        out[k] = v;
    });
    return out;
}

export interface UseDeviceBundleDraft {
    payload: DeviceBundlePayload;
    patchDevice: (patch: Partial<DeviceBundlePayload['device']>) => void;
    setPayload: (next: DeviceBundlePayload) => void;
    step: DeviceStepKey;
    goto: (s: DeviceStepKey) => void;
    /** 展示用报告 (已撤掉改过的那几条); 右栏与字段红字都用它 */
    report: BundleReport | null;
    /** 服务端最后一次原始判定: 能不能提交以它为准 */
    rawReport: BundleReport | null;
    /** 载荷在上次预检之后改过: 报告已过期, 不可作提交依据 (提交页会自动重跑) */
    stale: boolean;
    checking: boolean;
    error: string;
    runCheck: () => Promise<boolean>;
    /** 收下提交接口返回的报告 (提交被拒时以服务端那次判定为准) */
    adoptReport: (rep: BundleReport) => void;
    issuesByStep: Record<DeviceStepKey, BundleIssue[]>;
    issueByPath: IssueByPath;
    restoredAt: number;
    dismissRestored: () => void;
}

const severityRank: Record<string, number> = { error: 3, warning: 2, info: 1 };

export function useDeviceBundleDraft(): UseDeviceBundleDraft {
    const [payload, setPayloadRaw] =
        useState<DeviceBundlePayload>(emptyDevicePayload);
    const [step, setStep] = useState<DeviceStepKey>('basic');
    const [restoredAt, setRestoredAt] = useState(0);
    const payloadRef = useRef(payload);
    payloadRef.current = payload;
    // 草稿按登录用户分开存; me 还没加载完时 key 为空, 这一轮既不恢复也不落盘
    const { username } = useMe();
    const draftKey = draftKeyOf(username);
    const restoredRef = useRef(false);

    useEffect(() => {
        if (!draftKey || restoredRef.current) return;
        restoredRef.current = true;
        dropLegacyDraft();
        const d = loadDraft(username);
        if (d && hasContent(d.payload)) {
            setPayloadRaw(d.payload);
            setStep(d.step);
            setRestoredAt(d.savedAt);
        }
    }, [draftKey, username]);

    useEffect(() => {
        if (!draftKey || !hasContent(payload)) return;
        const t = window.setTimeout(() => {
            try {
                localStorage.setItem(
                    draftKey,
                    JSON.stringify({ payload, step, savedAt: Date.now() }),
                );
            } catch {
                /* 存不下就算了, 不打断填写 */
            }
        }, DRAFT_DEBOUNCE_MS);
        return () => window.clearTimeout(t);
    }, [payload, step, draftKey]);

    useEffect(() => {
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            if (!hasContent(payloadRef.current)) return;
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, []);

    const patchDevice = useCallback(
        (patch: Partial<DeviceBundlePayload['device']>) => {
            setPayloadRaw((cur) => ({
                ...cur,
                device: { ...cur.device, ...patch },
            }));
        },
        [],
    );

    // 预检状态 (报告/过期判定/提交页自动重跑) 与材料向导共用, 见 utils/useBundleCheck
    const check = useBundleCheck(payload, toRequest, submitDeviceBundle, step === 'review');
    const shownReport = check.report;

    const issuesByStep = useMemo(() => {
        const out: Record<DeviceStepKey, BundleIssue[]> = {
            basic: [],
            modes: [],
            fields: [],
            calib: [],
            params: [],
            review: [],
        };
        if (!shownReport) return out;
        [
            ...shownReport.errors,
            ...shownReport.warnings,
            ...shownReport.infos,
        ].forEach((i) => {
            out[stepOf(i.path)].push(i);
        });
        return out;
    }, [shownReport]);

    const issueByPath = useMemo(() => {
        const out: IssueByPath = {};
        if (!shownReport) return out;
        [
            ...shownReport.errors,
            ...shownReport.warnings,
            ...shownReport.infos,
        ].forEach((i) => {
            const cur = out[i.path];
            if (!cur || severityRank[i.severity] > severityRank[cur.severity]) {
                out[i.path] = i;
            }
        });
        return out;
    }, [shownReport]);

    return {
        payload,
        patchDevice,
        setPayload: setPayloadRaw,
        step,
        goto: setStep,
        report: shownReport,
        rawReport: check.rawReport,
        stale: check.stale,
        checking: check.checking,
        error: check.error,
        runCheck: check.runCheck,
        adoptReport: check.adoptReport,
        issuesByStep,
        issueByPath,
        restoredAt,
        dismissRestored: () => setRestoredAt(0),
    };
}
